// Parses Rules Definitions (Rules Glossary) text pasted from a PDF into separate definitions,
// for RuleDefinitionImporter. Kept free of model imports so it can be tested on its own.
//
// Text copied out of a PDF loses its formatting, so headings are recognized by their shape:
// a short Title Case line, optionally followed by a tag in brackets ("Blinded [Condition]"),
// that starts after the previous text finished a sentence. If that guesses wrong, start each
// heading line with "#" and only those lines will be used as headings.

export interface ParsedRuleDefinition {
  name: string;
  tag: string;              // What was in the brackets after the name ("Condition"), or "" for none.
  description: string;      // Without the trailing "See also" sentence, if one was found.
  see_also_names: string[]; // The names from the "See also" sentence, without tags.
  see_also_text: string;    // The "See also" sentence as it was, or "" for none.
}

// Words that stay lowercase in a Title Case heading.
const SMALL_WORDS = ["a","an","and","as","at","by","for","from","in","into","of","on","or","the","to","vs.","with"];
// Lines that are page furniture rather than content.
const NOISE_LINES: RegExp[] = [
  /^\d+$/,                                   // Page numbers
  /^system reference document/i,             // Page headers/footers
  /^rules (definitions|glossary)$/i,         // The chapter title
];
const HEADING_PATTERN = /^(.+?)(?:\s*\[([^\]]+)\])?$/;
const SENTENCE_END = /[.!?:"”’)\]]$/;
const BULLET = /^[•◦▪*-]\s/;

const cleanLines = (text: string): string[] => text
  .replace(/\r\n?/g, "\n")
  .replace(new RegExp(String.fromCharCode(160), "g"), " ") // Non-breaking spaces
  .split("\n")
  .map(line => line.replace(/\s+/g, " ").trim())
  .filter(line => !NOISE_LINES.some(pattern => pattern.test(line)));

const isTitleCase = (name: string): boolean => {
  const words = name.split(" ");
  if (words.length === 0 || words.length > 7 || !/^[A-Z0-9]/.test(words[0])) {
    return false;
  }
  return words.every(word => /^[A-Z0-9(]/.test(word) || SMALL_WORDS.includes(word));
};

const looksLikeHeading = (line: string, previous: string | null): boolean => {
  if (line.length === 0 || line.length > 60 || BULLET.test(line)) {
    return false;
  }
  // The previous text has to have finished (a heading never starts mid-sentence).
  if (previous !== null && previous !== "" && !SENTENCE_END.test(previous)) {
    return false;
  }
  const match = line.match(HEADING_PATTERN);
  const name = match ? match[1].trim() : line;
  if (/[,;—–]/.test(name) || SENTENCE_END.test(name) && !match?.[2]) {
    return false;
  }
  return isTitleCase(name);
};

// Joins wrapped lines back into paragraphs. Blank lines separate paragraphs,
// and bullet points start their own lines.
const joinBody = (lines: string[]): string => {
  const paragraphs: string[] = [];
  let current = "";
  const finish = () => {
    if (current !== "") {
      paragraphs.push(current);
      current = "";
    }
  };
  for (const line of lines) {
    if (line === "") {
      finish();
    } else if (BULLET.test(line)) {
      finish();
      current = line;
    } else if (current === "") {
      current = line;
    } else if (/[A-Za-z]-$/.test(current) && /^[a-z]/.test(line)) {
      // A word hyphenated across the line break.
      current = current.slice(0, -1) + line;
    } else {
      current += ` ${line}`;
    }
  }
  finish();
  return paragraphs.join("\n\n");
};

// Splits a trailing 'See also "Actions" and "Hiding."' off the description.
const splitSeeAlso = (description: string): { description: string, see_also_names: string[], see_also_text: string } => {
  const index = description.lastIndexOf("See also");
  if (index === -1) {
    return { description, see_also_names: [], see_also_text: "" };
  }
  const see_also_text = description.substring(index).trim();
  const list = see_also_text.substring("See also".length).trim();
  let names = Array.from(list.matchAll(/[“"]([^”"]+)[”"]/g)).map(m => m[1]);
  if (names.length === 0) {
    names = list.replace(/\.$/, "").split(/,\s*|\s+and\s+|\s+or\s+/);
  }
  names = names
    .map(name => name.trim().replace(/[.,;:]+$/, "").replace(/\s*\[[^\]]+\]$/, "").trim())
    .filter(name => name.length > 0);
  if (names.length === 0) {
    return { description, see_also_names: [], see_also_text: "" };
  }
  return {
    description: description.substring(0, index).trim(),
    see_also_names: names,
    see_also_text,
  };
};

export function parseRuleDefinitions(text: string): ParsedRuleDefinition[] {
  const lines = cleanLines(text);
  const explicit = lines.some(line => line.startsWith("#"));
  const sections: { heading: string, body: string[] }[] = [];
  let previous: string | null = null;
  for (const line of lines) {
    const is_heading = explicit ? line.startsWith("#") : looksLikeHeading(line, previous);
    if (is_heading) {
      sections.push({ heading: line.replace(/^#+\s*/, ""), body: [] });
    } else if (sections.length > 0) {
      sections[sections.length - 1].body.push(line);
    }
    // Anything before the first heading (an introduction) is left out.
    if (line !== "" || previous !== null) {
      previous = line;
    }
  }
  return sections.map(section => {
    const match = section.heading.match(HEADING_PATTERN);
    const name = match ? match[1].trim() : section.heading;
    const tag = match?.[2]?.trim() ?? "";
    return { name, tag, ...splitSeeAlso(joinBody(section.body)) };
  });
}
